import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Bell, Check, ExternalLink } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const TYPE_ICON = {
  bozza_da_approvare: "bg-orange-100 text-orange-600",
  offerta_approvata: "bg-green-100 text-green-600",
  offerta_respinta: "bg-red-100 text-red-600",
  offerta_inviata: "bg-purple-100 text-purple-600",
  offerta_accettata: "bg-green-100 text-green-700",
  offerta_rifiutata_pubblica: "bg-red-100 text-red-600",
};

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, []);

  const openItem = async (n) => {
    if (!n.letto) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    if (n.link) navigate(n.link);
    setOpen(false);
    load();
  };
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  const iconCls = (t) => Object.entries(TYPE_ICON).find(([k]) => t?.startsWith(k))?.[1] || "bg-slate-100 text-slate-600";

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-slate-100 text-slate-700" data-testid="notif-bell">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] px-1 rounded-full bg-[#FACC15] text-[#0F2C59] text-[10px] font-bold flex items-center justify-center border-2 border-white" data-testid="notif-count">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0 max-h-[70vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="font-display font-semibold text-[#0F2C59]">Notifiche</div>
            {data.unread > 0 && <div className="text-xs text-slate-500">{data.unread} non lette</div>}
          </div>
          {data.unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} data-testid="mark-all-read-btn">
              <Check className="w-3.5 h-3.5 mr-1" /> Segna tutte
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              Nessuna notifica
            </div>
          ) : (
            data.items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 flex gap-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${!n.letto ? "bg-blue-50/40" : ""}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconCls(n.tipo)}`}>
                  <Bell className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${!n.letto ? "font-semibold text-[#0F2C59]" : "text-slate-700"}`}>{n.titolo}</div>
                  {n.messaggio && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.messaggio}</div>}
                  <div className="text-[11px] text-slate-400 font-mono mt-1">{fmtDateTime(n.created_at)}</div>
                </div>
                {n.link && <ExternalLink className="w-3.5 h-3.5 text-slate-400 mt-1" />}
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
